import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Globe, Sparkles, FileText, Loader2, BookOpen, Check, Languages, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import StoryInspiration from '@/components/create-book/StoryInspiration';
import { TitleTranslationHelper } from '@/components/create-book/TitleTranslationHelper';
import { getOfferedLanguageOptions, getLanguageByCode } from '@/utils/languages';
import { useCountryDetection } from '@/utils/countryDetection';
import PriceDisplay from '@/components/pricing/PriceDisplay';
import LocalPrice from '@/components/pricing/LocalPrice';
import { cn } from '@/lib/utils';
import { DEDICATION_MAX_CHARS } from '@/lib/books/dedication';
import { InlineHelp } from '../WizardTooltip';
import { useToast } from '@/hooks/use-toast';
import { UNIFIED_STORY_TEMPLATES, personalizeTitle, getTemplateTitle, filterCommentsForCharacterCount, type UnifiedStoryTemplate } from '@/lib/unified-story-templates';
import { StoryTemplatePicker } from '../StoryTemplatePicker';
import { allowedStoryPageCounts, DEFAULT_STORY_PAGE_COUNT } from '@/lib/pricing/page-length-policy';
import { ReadingLevelPicker } from '../ReadingLevelPicker';

// Story time's longer text needs its own page facing the picture. The A5 and digital layouts still print
// text over the picture, so the choice stays hidden (every book is a Picture book) until that layout ships.
const SHOW_READING_LEVEL = false;
import { DEFAULT_READING_LEVEL, type ReadingLevel } from '@/lib/story/reading-level';
import { track } from '@/utils/analytics';
import { getResolvedStoryPageCounts, loadStoryPageCounts } from '@/lib/pricing/story-page-counts-client';

interface StoryCreationStepProps {
  language: string;
  bookTitle: string;
  additionalComments: string;
  pageCount: number;
  culturalSetting?: string;
  culturalDetails?: string;
  onLanguageChange: (language: string) => void;
  onTitleChange: (title: string) => void;
  onCommentsChange: (comments: string) => void;
  dedication?: string;
  onDedicationChange?: (v: string) => void;
  dedicationError?: string | null;
  onPageCountChange: (count: number) => void;
  readingLevel?: ReadingLevel;
  onReadingLevelChange?: (level: ReadingLevel) => void;
  onCulturalSettingChange?: (setting: string) => void;
  onCulturalDetailsChange?: (details: string) => void;
  isMultiCharacter?: boolean;
  // Character info for template personalization
  characterName?: string; // For single character
  characters?: Array<{ name: string; isMainCharacter?: boolean }>; // For multi-character
  /** Fast path: occasion groups first, free text behind "write your own". */
  occasionFirst?: boolean;
  /** Pre-selected template (free-cover continuation). */
  initialTemplateId?: string | null;
  /** Tells the wizard which template was picked, so its conflict/antagonist reach story generation. */
  onTemplateSelect?: (template: UnifiedStoryTemplate | null) => void;
  /** Template the wizard currently holds (so a remount does not re-announce it). */
  heldTemplateId?: string | null;
  errors?: {
    language?: string;
    bookTitle?: string;
    additionalComments?: string;
  };
}

export function StoryCreationStep({
  language,
  bookTitle,
  additionalComments,
  pageCount,
  culturalSetting,
  culturalDetails,
  onLanguageChange,
  onTitleChange,
  onCommentsChange,
  dedication,
  onDedicationChange,
  dedicationError,
  onPageCountChange,
  readingLevel = DEFAULT_READING_LEVEL,
  onReadingLevelChange,
  onCulturalSettingChange,
  onCulturalDetailsChange,
  isMultiCharacter = false,
  characterName,
  characters,
  occasionFirst = false,
  initialTemplateId = null,
  onTemplateSelect,
  heldTemplateId = null,
  errors = {}
}: StoryCreationStepProps) {
  const [showStoryIdeas, setShowStoryIdeas] = useState(false);
  const [showTemplates, setShowTemplates] = useState(occasionFirst);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(initialTemplateId);
  // Occasion-first hides the free-text fields until a template is chosen or the visitor asks to write their own.
  const [showOwnStory, setShowOwnStory] = useState(!occasionFirst || !!additionalComments);
  // The concept text the picked template last wrote. If the visitor rewrites it, the template's
  // conflict/antagonist must stop reaching story generation (a bedtime rewrite shouldn't get a villain).
  const prefilledRef = useRef<string | null>(initialTemplateId ? additionalComments.trim() : null);
  const applyingTemplateRef = useRef(false);

  // A template handed in by the parent (free-cover continuation) that the wizard doesn't hold yet.
  useEffect(() => {
    if (!initialTemplateId || initialTemplateId === heldTemplateId) return;
    const t = UNIFIED_STORY_TEMPLATES.find((x) => x.id === initialTemplateId);
    if (t) onTemplateSelect?.(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rewriting the concept after picking a template drops the template (and its conflict data).
  useEffect(() => {
    if (!selectedTemplateId || applyingTemplateRef.current || prefilledRef.current === null) return;
    if (additionalComments.trim() !== prefilledRef.current) {
      prefilledRef.current = null;
      setSelectedTemplateId(null);
      onTemplateSelect?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalComments, selectedTemplateId]);

  // Fast arm: a blocked Next must show why. Reveal the hidden fields when validation errors arrive.
  useEffect(() => {
    if (occasionFirst && (errors.bookTitle || errors.additionalComments)) setShowOwnStory(true);
  }, [occasionFirst, errors.bookTitle, errors.additionalComments]);
  const { country } = useCountryDetection();
  const [pricingData, setPricingData] = useState<Record<number, any>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [isTranslatingDetails, setIsTranslatingDetails] = useState(false);
  const [isTranslatingTitle, setIsTranslatingTitle] = useState(false);
  const [isAdaptingTemplate, setIsAdaptingTemplate] = useState(false);
  const { toast } = useToast();

  // Adapt story outline to character count (for multi-character stories)
  const adaptStoryOutline = useCallback(async (comments: string): Promise<string> => {
    // Only adapt for multi-character stories with 2+ characters
    if (!isMultiCharacter || !characters || characters.length <= 1) {
      return comments;
    }

    const characterNames = characters.map(c => c.name);

    try {
      setIsAdaptingTemplate(true);
      console.log(`📝 [UI] Adapting outline for ${characterNames.length} characters: ${characterNames.join(', ')}`);

      const response = await fetch('/api/books/adapt-story-outline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          additionalComments: comments,
          characterNames
        })
      });

      if (!response.ok) {
        console.warn('⚠️ [UI] Adaptation API failed, using original');
        return comments;
      }

      const data = await response.json();
      console.log(`✅ [UI] Adaptation complete: ${data.originalLength} → ${data.adaptedLength} chars`);
      return data.adaptedComments || comments;
    } catch (error) {
      console.error('❌ [UI] Adaptation error:', error);
      return comments;
    } finally {
      setIsAdaptingTemplate(false);
    }
  }, [isMultiCharacter, characters]);

  // Track previous language for auto-translation
  const previousLanguageRef = useRef<string>('en');
  const lastTranslatedDetailsRef = useRef<string>('');
  const lastTranslatedTitleRef = useRef<string>('');

  // Auto-translate title when language changes
  const translateTitle = useCallback(async (title: string, langCode: string, langName: string, storyDetails = '') => {
    if (!title.trim() || title.length < 3) {
      return;
    }

    setIsTranslatingTitle(true);

    try {
      const response = await fetch('/api/translate-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: title,
          targetLanguage: langCode,
          type: 'book_title',
          context: storyDetails // so the title uses the story's variant (pt) and name spellings (ja)
        })
      });

      if (!response.ok) {
        throw new Error('Translation failed');
      }

      const data = await response.json();

      if (data.translatedText) {
        onTitleChange(data.translatedText);
        toast({
          title: "Title translated!",
          description: `Translated to ${langName}`,
        });
      }
    } catch (error) {
      console.error('Title translation error:', error);
      toast({
        title: "Translation failed",
        description: "Could not translate title. You can edit it manually.",
        variant: "destructive"
      });
    } finally {
      setIsTranslatingTitle(false);
    }
  }, [onTitleChange, toast]);

  // Auto-translate story details when language changes
  const translateStoryDetails = useCallback(async (details: string, langCode: string, langName: string) => {
    if (!details.trim() || details.length < 5) {
      return;
    }

    setIsTranslatingDetails(true);

    try {
      const response = await fetch('/api/translate-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: details,
          targetLanguage: langCode,
          type: 'story_details'
        })
      });

      if (!response.ok) {
        throw new Error('Translation failed');
      }

      const data = await response.json();

      if (data.translatedText) {
        if (prefilledRef.current === details.trim()) prefilledRef.current = String(data.translatedText).trim();
        onCommentsChange(data.translatedText);
        toast({
          title: "Story details translated!",
          description: `Translated to ${langName}`,
        });
      }
    } catch (error) {
      console.error('Story details translation error:', error);
      toast({
        title: "Translation failed",
        description: "Could not translate story details. You can edit them manually.",
        variant: "destructive"
      });
    } finally {
      setIsTranslatingDetails(false);
    }
  }, [onCommentsChange, toast]);

  // Auto-translate when language changes to non-English
  useEffect(() => {
    const detailsToTranslate = additionalComments.trim();
    const titleToTranslate = bookTitle.trim();
    const languageChanged = language !== previousLanguageRef.current;
    const isNonEnglish = language !== 'en';

    if (languageChanged && isNonEnglish) {
      const langInfo = getLanguageByCode(language);
      if (langInfo) {
        console.log(`[Translation] Language changed to ${language}, translating...`);
        previousLanguageRef.current = language;

        // Translate title if it exists and hasn't been translated yet
        if (titleToTranslate.length >= 3 &&
            lastTranslatedTitleRef.current !== `${titleToTranslate}:${language}`) {
          console.log(`[TitleTranslation] Auto-translating title to ${language}`);
          lastTranslatedTitleRef.current = `${titleToTranslate}:${language}`;
          translateTitle(titleToTranslate, language, langInfo.nativeName, detailsToTranslate);
        }

        // Translate story details if they exist and haven't been translated yet
        if (detailsToTranslate.length >= 5 &&
            lastTranslatedDetailsRef.current !== `${detailsToTranslate}:${language}`) {
          console.log(`[StoryDetailsTranslation] Auto-translating to ${language}`);
          lastTranslatedDetailsRef.current = `${detailsToTranslate}:${language}`;
          translateStoryDetails(detailsToTranslate, language, langInfo.nativeName);
        }
      }
    } else if (languageChanged) {
      previousLanguageRef.current = language;
    }
  }, [language, additionalComments, bookTitle, translateStoryDetails, translateTitle]);
  
  // Initialize with pageCount prop, but ensure it's a valid option. Default to 16.
  // Offered lengths depend on the visitor's country (8 pages only in low-price countries). The geo answer is cached at
  // module level, so remounts start from the resolved list; until it resolves we show [16, 24] and do not correct anything.
  const [allowedCounts, setAllowedCounts] = useState<number[]>(() => getResolvedStoryPageCounts() ?? allowedStoryPageCounts(null));
  const [geoResolved, setGeoResolved] = useState(() => getResolvedStoryPageCounts() !== null);
  const [selectedPageCount, setSelectedPageCount] = useState(
    allowedCounts.includes(pageCount) ? pageCount : DEFAULT_STORY_PAGE_COUNT
  );

  useEffect(() => {
    let cancelled = false;
    loadStoryPageCounts().then((counts) => {
      if (cancelled) return;
      setAllowedCounts(counts);
      setGeoResolved(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Once geo has resolved, correct a length that is not offered here (e.g. 8 from a template) so form and server agree.
  useEffect(() => {
    if (geoResolved && pageCount && !allowedCounts.includes(pageCount)) {
      setSelectedPageCount(DEFAULT_STORY_PAGE_COUNT);
      onPageCountChange(DEFAULT_STORY_PAGE_COUNT);
    }
  }, [pageCount, allowedCounts, geoResolved]);

  // Sync with pageCount prop when it changes
  useEffect(() => {
    console.log('StoryCreationStep - pageCount prop changed:', pageCount);
    // Only update if pageCount is an offered value
    if (pageCount && allowedCounts.includes(pageCount) && pageCount !== selectedPageCount) {
      console.log('StoryCreationStep - Updating selectedPageCount to:', pageCount);
      setSelectedPageCount(pageCount);
    }
  }, [pageCount, allowedCounts]);
  
  // Debug logging to check the pageCount value
  useEffect(() => {
    console.log('StoryCreationStep - Current pageCount:', pageCount, 'selectedPageCount:', selectedPageCount);
  }, [pageCount, selectedPageCount]);
  
  const selectedLanguage = getLanguageByCode(language);
  
  // Page count options - 16 and 24 pages everywhere, plus 8 in low-price countries (see page-length-policy)
  const PAGE_COUNT_OPTIONS = allowedCounts.map((n) => ({
    value: n,
    label: `${n} pages`,
    recommended: n === DEFAULT_STORY_PAGE_COUNT,
  }));
  
  // Fetch pricing for all page counts when country changes or component mounts
  useEffect(() => {
    const fetchAllPricing = async () => {
      if (!country) return;
      
      setLoadingPrices(true);
      const newPricingData: Record<number, any> = {};
      
      try {
        // Fetch pricing for all page count options in parallel
        const pricingPromises = PAGE_COUNT_OPTIONS.map(async (option) => {
          const params = new URLSearchParams({
            pageCount: option.value.toString(),
            shippingCountry: country.code,
            isMultiCharacter: isMultiCharacter.toString()
          });
          
          const response = await fetch(`/api/pricing/calculate?${params}`);
          const data = await response.json();
          return { pageCount: option.value, data };
        });
        
        const results = await Promise.all(pricingPromises);
        
        // Store pricing data by page count
        results.forEach(({ pageCount, data }) => {
          newPricingData[pageCount] = data;
        });
        
        setPricingData(newPricingData);
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Fall back to calculated prices if API fails
        PAGE_COUNT_OPTIONS.forEach((option) => {
          const basePrice = isMultiCharacter ? 12.94 : 9.95;
          const additionalPages = Math.max(0, option.value - 8);
          const perPagePrice = isMultiCharacter ? 1.30 : 1.00;
          const total = basePrice + (additionalPages * perPagePrice);
          newPricingData[option.value] = { total };
        });
        setPricingData(newPricingData);
      } finally {
        setLoadingPrices(false);
      }
    };
    
    fetchAllPricing();
  }, [country, isMultiCharacter, allowedCounts]);
  
  // Get price display for a specific page count
  const getPriceDisplay = (pageCountValue: number) => {
    const pricing = pricingData[pageCountValue];
    
    if (loadingPrices) {
      return <div className="animate-pulse h-5 w-16 bg-purple-200 rounded" />;
    }
    
    if (pricing) {
      return (
        <PriceDisplay
          amount={pricing.total || pricing.basePDF || 0}
          localized={pricing.localized?.total}
          size="sm"
          showUSD={false}
          className="font-bold text-purple-600"
        />
      );
    }
    
    // Fallback calculation
    const basePrice = isMultiCharacter ? 12.94 : 9.95;
    const additionalPages = Math.max(0, pageCountValue - 8);
    const perPagePrice = isMultiCharacter ? 1.30 : 1.00;
    const total = basePrice + (additionalPages * perPagePrice);
    
    return (
      <span className="font-bold text-purple-600">
        <LocalPrice usd={total} />
      </span>
    );
  };
  
  const charList = characters?.length ? characters : (characterName ? [{ name: characterName }] : []);
  const titleFor = (template: UnifiedStoryTemplate) =>
    charList.length > 0
      ? personalizeTitle(template, charList)
      : getTemplateTitle(template, 1).replace(/\[\[NAME\]\]/g, 'Your Child');

  const handleTemplateSelect = async (template: UnifiedStoryTemplate) => {
    applyingTemplateRef.current = true;
    setSelectedTemplateId(template.id);
    onTemplateSelect?.(template);
    onTitleChange(titleFor(template));
    setShowOwnStory(true);

    // For multi-character stories, adapt the template to actual character count
    const rawComments = template.storyContext.additionalComments;
    const characterCount = isMultiCharacter && characters ? characters.length : 1;
    if (isMultiCharacter && characters && characters.length > 1) {
      onCommentsChange(rawComments + '\n\n⏳ Adapting to your characters...');
      const adaptedComments = await adaptStoryOutline(rawComments);
      prefilledRef.current = adaptedComments.trim();
      onCommentsChange(adaptedComments);
    } else {
      const filtered = filterCommentsForCharacterCount(rawComments, characterCount);
      prefilledRef.current = filtered.trim();
      onCommentsChange(filtered);
    }
    applyingTemplateRef.current = false;

    // Classic layout collapses after a pick; occasion-first keeps the cards in view.
    if (!occasionFirst) setTimeout(() => setShowTemplates(false), 300);
  };

  return (
    <div className="space-y-6">
      {/* STEP 1: Story Template Selection - Collapsible */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
          <BookOpen className="w-4 h-4" />
          {occasionFirst ? "What's the occasion?" : 'Choose a Story Template'}
        </Label>
        <p className="text-sm text-gray-600 ml-8">
          {occasionFirst
            ? 'Pick a moment to celebrate. You can edit the title and story afterwards.'
            : 'Select a template to auto-fill title and story details, or skip to create your own story.'}
        </p>

        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal",
            showTemplates && "bg-purple-50 border-purple-300",
            selectedTemplateId && !showTemplates && "border-purple-400 bg-purple-50"
          )}
          onClick={() => setShowTemplates(!showTemplates)}
        >
          <span className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            {selectedTemplateId
              ? `✓ Template Selected`
              : 'Browse Story Templates'
            }
          </span>
          <motion.div
            animate={{ rotate: showTemplates ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-4 h-4" />
          </motion.div>
        </Button>

        <AnimatePresence>
          {showTemplates && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-3 pb-2">
                <div className={cn('pr-1', !occasionFirst && 'max-h-[400px] overflow-y-auto')}>
                  <StoryTemplatePicker
                    templates={UNIFIED_STORY_TEMPLATES}
                    occasionFirst={occasionFirst}
                    selectedId={selectedTemplateId}
                    titleFor={titleFor}
                    onSelect={handleTemplateSelect}
                    onWriteOwn={() => { setSelectedTemplateId(null); onTemplateSelect?.(null); setShowOwnStory(true); setShowTemplates(false); }}
                  />
                </div>
                {selectedTemplateId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-gray-500"
                    onClick={() => {
                      setSelectedTemplateId(null);
                      onTemplateSelect?.(null);
                      // Don't clear fields - let user keep editing
                    }}
                  >
                    Clear template selection
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* STEP 2: Language selector */}
      <div className="space-y-2">
        <Label htmlFor="language" className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
          <Globe className="w-4 h-4" />
          Select Story Language
          <InlineHelp content="Choose the language for your story text. The book will be written entirely in this language." />
        </Label>
        <p className="text-sm text-gray-600 ml-8">
          Your book title and story details will be automatically translated when you select a different language.
        </p>

        <Select key={language} value={language} onValueChange={onLanguageChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a language" />
          </SelectTrigger>
          <SelectContent>
            {getOfferedLanguageOptions(language).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.language && (
          <p className="text-sm text-red-500">{errors.language}</p>
        )}

        {/* Prominent language indicator */}
        {selectedLanguage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "rounded-lg p-4 mt-3",
              selectedLanguage.code === 'en'
                ? "bg-gray-50 border border-gray-200"
                : "bg-blue-50 border-2 border-blue-400"
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Globe className={cn(
                  "w-6 h-6",
                  selectedLanguage.code === 'en' ? "text-gray-500" : "text-blue-600"
                )} />
              </div>
              <div className="flex-1">
                <p className={cn(
                  "text-sm font-semibold",
                  selectedLanguage.code === 'en' ? "text-gray-700" : "text-blue-900"
                )}>
                  Your story will be generated in <span aria-hidden="true">{selectedLanguage.flag}</span> <span className="font-bold">{selectedLanguage.nativeName}</span>
                </p>
                <p className={cn(
                  "text-xs mt-1",
                  selectedLanguage.code === 'en' ? "text-gray-600" : "text-blue-700"
                )}>
                  All story text, dialogue, descriptions, and the book title will be in {selectedLanguage.name}
                </p>
                {selectedLanguage.code !== 'en' && (isTranslatingTitle || isTranslatingDetails) && (
                  <p className="text-xs text-blue-600 mt-2 font-medium flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Translating your content...
                  </p>
                )}
                {selectedLanguage.code !== 'en' && !isTranslatingTitle && !isTranslatingDetails && (
                  <p className="text-xs text-blue-600 mt-2 font-medium">
                    ✨ Title and story details translated to {selectedLanguage.name}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {showOwnStory ? (<>
      {/* STEP 3: Book Title */}
      <div className="space-y-2">
        <Label htmlFor="title" className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
          Book Title
          <InlineHelp content="Give your story a memorable title. You can use the suggestions below for inspiration." />
          {isTranslatingTitle && (
            <span className="flex items-center text-xs text-purple-600 ml-2">
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
              Translating...
            </span>
          )}
        </Label>

        {/* Expandable story ideas */}
        <motion.div
          className="mb-3 ml-8"
          initial={false}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowStoryIdeas(!showStoryIdeas)}
            className="text-purple-600 hover:text-purple-700 p-0 h-auto font-medium"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            {showStoryIdeas ? 'Hide' : 'Need'} story ideas?
            <motion.div
              animate={{ rotate: showStoryIdeas ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4 ml-1" />
            </motion.div>
          </Button>
        </motion.div>

        <AnimatePresence>
          {showStoryIdeas && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mb-4">
                <StoryInspiration
                  onSelectTitle={onTitleChange}
                  onSelectStory={onCommentsChange}
                  onSelectBoth={(title, story) => {
                    onTitleChange(title);
                    onCommentsChange(story);
                    setShowStoryIdeas(false);
                  }}
                  variant="compact"
                  showCulturalStories={!!culturalSetting}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Input
          id="title"
          type="text"
          value={bookTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          onBlur={(e) => onTitleChange(e.target.value.trim())}
          placeholder={selectedLanguage?.titlePlaceholder || "Enter your story title"}
          className={cn(
            "text-lg",
            selectedLanguage?.rtl ? 'text-right' : 'text-left',
            errors.bookTitle && "border-red-500 focus:ring-red-500",
            isTranslatingTitle && "opacity-60"
          )}
          dir={selectedLanguage?.rtl ? 'rtl' : 'ltr'}
          disabled={isTranslatingTitle}
        />
        {!bookTitle && (
          <p className="text-red-500 text-sm mt-1">Book title is required (minimum 3 characters)</p>
        )}
        {bookTitle && bookTitle.length < 3 && (
          <p className="text-red-500 text-sm mt-1">Book title must be at least 3 characters long</p>
        )}

        {/* Translation helper - only show if not auto-translating */}
        {selectedLanguage && selectedLanguage.code !== 'en' && !isTranslatingTitle && (
          <TitleTranslationHelper
            currentTitle={bookTitle}
            targetLanguageCode={selectedLanguage.code}
            targetLanguageName={selectedLanguage.nativeName}
            storyDetails={additionalComments}
            onSuggestedTitle={onTitleChange}
          />
        )}

        {errors.bookTitle && (
          <p className="text-sm text-red-500">{errors.bookTitle}</p>
        )}
      </div>

      {/* STEP 4: Story details */}
      <div className="space-y-2">
        <Label htmlFor="details" className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
          Story Details
          <InlineHelp content="Describe what you'd like to happen in the story. Include themes, lessons, or specific adventures you want." />
          {isTranslatingDetails && (
            <span className="flex items-center text-xs text-purple-600 ml-2">
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
              Translating...
            </span>
          )}
        </Label>
        <Textarea
          id="details"
          value={additionalComments}
          onChange={(e) => onCommentsChange(e.target.value)}
          onBlur={(e) => onCommentsChange(e.target.value.trim())}
          placeholder={selectedLanguage?.detailsPlaceholder || "What adventures should happen in your story?"}
          className={cn(
            "min-h-[120px] resize-none",
            selectedLanguage?.rtl ? 'text-right' : 'text-left',
            errors.additionalComments && "border-red-500 focus:ring-red-500",
            isTranslatingDetails && "opacity-60"
          )}
          dir={selectedLanguage?.rtl ? 'rtl' : 'ltr'}
          disabled={isTranslatingDetails}
        />
        {!additionalComments && (
          <p className="text-red-500 text-sm mt-1">Story details are required (minimum 5 characters)</p>
        )}
        {additionalComments && additionalComments.length < 5 && (
          <p className="text-red-500 text-sm mt-1">Story details must be at least 5 characters long</p>
        )}
        {errors.additionalComments && (
          <p className="text-sm text-red-500">{errors.additionalComments}</p>
        )}

        <div className="mt-6">
          <Label htmlFor="dedication">Dedication (optional)</Label>
          <p className="text-sm text-gray-500 mb-2">Printed on its own page at the front of the book.</p>
          <Textarea
            id="dedication"
            value={dedication ?? ''}
            onChange={(e) => onDedicationChange?.(e.target.value)}
            placeholder="For Maya, who makes every day an adventure. Love, Grandma"
            rows={3}
            dir="auto"
            aria-describedby="dedication-help"
          />
          <div id="dedication-help" className="flex justify-between text-xs mt-1">
            <span className="text-red-600">{dedicationError ?? ''}</span>
            <span className="text-gray-500">{Array.from(dedication ?? '').length}/{DEDICATION_MAX_CHARS}</span>
          </div>
        </div>
      </div>
      
      </>) : (
        <p className="text-sm text-gray-600">
          {(errors.bookTitle || errors.additionalComments) && (
            <span className="mb-1 block text-red-600">Pick an occasion, or write your own story idea, to continue.</span>
          )}
          Pick an occasion above, or{' '}
          <button type="button" className="font-medium text-purple-700 hover:underline" onClick={() => setShowOwnStory(true)}>
            write your own story idea
          </button>.
        </p>
      )}

      {/* STEP 5: Reading level (hidden until text pages face the pictures, owner 2026-10-17) */}
      {SHOW_READING_LEVEL && (
      <div className="space-y-3">
        <Label className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
          <BookOpen className="w-4 h-4" />
          Reading level
        </Label>
        <ReadingLevelPicker
          value={readingLevel}
          onChange={(l) => {
            onReadingLevelChange?.(l);
            track('wizard_step_viewed', { step: 'reading_level', reading_level: l });
          }}
        />
      </div>
      )}

      {/* STEP 6: Page Count Selection */}
      <div className="space-y-3">
        <Label className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">{SHOW_READING_LEVEL ? 6 : 5}</span>
          <FileText className="w-4 h-4" />
          Story Length
          <InlineHelp content="Choose how many pages your story will have. More pages allow for longer, more detailed adventures with richer storytelling and character development." />
        </Label>

        {/* Guidance text */}
        <p className="text-sm text-gray-600 ml-8">
          {allowedCounts.includes(8) && (<><strong>8 pages:</strong> Perfect for bedtime stories<br /></>)}
          <strong>16 pages:</strong> Great for deeper adventures<br />
          <strong>24 pages:</strong> Epic journeys with multiple scenes
        </p>
        
        {/* Payment reassurance message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <div className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <div className="text-sm text-green-700">
              <strong>No payment required yet!</strong> You'll see a complete preview of your book cover 
              before making any payment decision. Feel free to explore all options.
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {PAGE_COUNT_OPTIONS.map((option) => (
            <motion.div
              key={option.value}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card
                className={cn(
                  "p-3 cursor-pointer transition-all relative",
                  selectedPageCount === option.value // Use local state for immediate feedback
                    ? 'border-2 border-purple-600 bg-purple-100 ring-2 ring-purple-600 ring-offset-2'
                    : 'border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                )}
                onClick={() => {
                  setSelectedPageCount(option.value); // Immediate visual feedback
                  onPageCountChange(option.value);
                }}
              >
                <div className="space-y-1">
                  <div className={cn(
                    "font-medium",
                    selectedPageCount === option.value ? "text-purple-900" : "text-gray-700"
                  )}>
                    {option.label}
                  </div>
                  <div className={cn(
                    "text-lg font-bold",
                    selectedPageCount === option.value ? "text-purple-700" : "text-purple-600"
                  )}>
                    {getPriceDisplay(option.value)}
                  </div>
                  <div className={cn(
                    "text-xs",
                    selectedPageCount === option.value ? "text-purple-600" : "text-gray-500"
                  )}>
                    Preview first, pay later
                  </div>
                  {/* Add checkmark for selected state */}
                  {selectedPageCount === option.value && (
                    <div className="absolute top-1 right-1">
                      <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Advanced options modal - removed for cleaner UI */}
    </div>
  );
}
